import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Cigarette, Heart, Brain } from 'lucide-react';
import CTA from '../../components/CTA';

const CraigavonQuitSmoking: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Quit Smoking Craigavon | Stop Vaping Hypnotherapy Lurgan Portadown</title>
        <meta
          name="description"
          content="Quit smoking hypnotherapy serving Craigavon from Belfast clinic (25 miles). Intensive 2-hour session with lifetime support. Honest success rates 20-30% at 12 months."
        />
        <meta name="keywords" content="quit smoking Craigavon, stop smoking Lurgan, hypnotherapy smoking cessation Portadown, quit vaping Craigavon, smoking hypnotherapy Craigavon" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/craigavon-quit-smoking/" />
        <meta property="og:title" content="Quit Smoking Craigavon | Stop Vaping Hypnotherapy Lurgan Portadown" />
        <meta property="og:description" content="Intensive 2-hour quit smoking hypnotherapy serving Craigavon. Honest success rates, transparent pricing, lifetime support." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Smoking Cessation Craigavon",
            "description": "CNHC registered hypnotherapy for smoking cessation, serving Craigavon, Lurgan, and Portadown from Belfast clinic. Intensive 2-hour session with lifetime support and money-back guarantee.",
            "url": "https://www.sharonclare-hypnotherapy.com/craigavon-quit-smoking/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Smoking Cessation Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Craigavon",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does quit smoking hypnotherapy cost in Craigavon?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The intensive 2-hour quit smoking session includes lifetime support and a money-back guarantee. For clients who prefer a gradual approach, a 3-session package is also available. To put this in perspective: a 20-a-day smoker spends thousands of pounds a year on cigarettes. You recoup the cost of the session in just 3 weeks of not smoking. The financial return alone makes this one of the best investments you can make for Craigavon families facing economic pressures."
                }
              },
              {
                "@type": "Question",
                "name": "What are the real success rates for quit smoking hypnotherapy?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I'm honest about success rates because trust matters. Research shows that at 12 months, approximately 20-30% of motivated clients remain smoke-free with hypnotherapy support. While some studies report higher short-term rates, long-term success is more modest. Hypnotherapy isn't magic - it works best when you're truly ready to become a non-smoker. The clients who succeed are those who recognize they're ready for change and use hypnotherapy as the tool to make it happen."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for commuter smoking patterns?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Many Craigavon residents who commute to Belfast via M1 develop strong car smoking habits - cigarettes during the drive, smoking at red lights, stress-triggered smoking in traffic. Hypnotherapy addresses these specific triggers by rewiring the subconscious patterns that link driving stress with cigarettes. We work on changing your association between car commutes and smoking, developing alternative stress management for traffic, and breaking the automatic reach for cigarettes during your drive. The approach is tailored to your specific smoking triggers."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help me quit vaping as well as smoking?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. The approach works equally well for vaping cessation as it does for cigarettes. While vaping is marketed as 'healthier' than smoking, it still maintains the nicotine addiction and the psychological habit. Many people who switched from smoking to vaping find themselves just as dependent. The hypnotherapy program addresses both the physical nicotine addiction and the psychological patterns that keep you reaching for your vape throughout the day."
                }
              },
              {
                "@type": "Question",
                "name": "How far is Belfast from Craigavon for quit smoking sessions?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Belfast is approximately 25 miles from Craigavon via the M1 motorway, taking 30-35 minutes by car. The journey is straightforward from Junction 10, and many Craigavon clients find it therapeutic - time to mentally prepare for becoming a non-smoker. The manageable distance makes professional smoking cessation support accessible for residents of Northern Ireland's largest town. Online sessions are also available if you prefer the convenience of attending from home."
                }
              },
              {
                "@type": "Question",
                "name": "Why is the ROI emphasis important for Craigavon families?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Economic pressures in Craigavon and working-class families make the financial impact of smoking particularly significant. A 20-a-day habit costs thousands of pounds a year - that's money that could go toward family expenses, savings, or quality of life improvements. Quitting smoking pays for itself in less than a month. For families facing economic challenges, quitting isn't just about health - it's about financial freedom and setting a better example for children."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Quit Smoking Hypnotherapy for Craigavon
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Intensive 2-hour session with lifetime support and money-back guarantee
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">25 miles</span> from Craigavon via M1 • <span className="font-semibold">30-35 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions available • Weekend appointments</p>
            </div>
            <div className="bg-white rounded-xl p-6 mb-8 max-w-2xl mx-auto">
              <div className="flex items-start space-x-4">
                <Heart className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div className="text-left">
                  <p className="text-lg font-semibold text-gray-900 mb-2">Investment That Pays for Itself</p>
                  <p className="text-gray-700">
                    <span className="font-semibold">One intensive session</span> vs. <span className="font-semibold">thousands of pounds a year on cigarettes</span>
                  </p>
                  <p className="text-accent-700 font-medium mt-1">Recoup your investment in just 3 weeks</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Quit Smoking for Good in Craigavon
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Craigavon, Lurgan, or Portadown and ready to quit smoking or vaping, you don't need to search for local support that might not exist. I serve the Craigavon borough from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, located approximately 25 miles north via the M1 motorway from Junction 10. The journey takes 30-35 minutes by car, making professional smoking cessation support genuinely accessible to residents of Northern Ireland's largest town.
              </p>
              <p>
                Craigavon is unique - a planned "new town" linking Lurgan and Portadown, with over 67,000 residents spread across car-dependent suburban developments. The working-class demographics, economic pressures on families, and the stress of commuter lifestyles all contribute to smoking patterns. You've probably tried before - maybe multiple times. Patches, gum, willpower, vaping as a "healthier alternative." But here you are, still smoking, still spending money you can't afford, still feeling controlled by cigarettes.
              </p>
              <p>
                Here's the truth: quitting smoking isn't about willpower. If it were, you'd have quit already. The physical addiction to nicotine is actually relatively short-lived - it's out of your system within 72 hours. What keeps you smoking is the psychological habit: the automatic reach for a cigarette during your M1 commute, the stress relief during a difficult day at work, the smoking break ritual, the identity of being "a smoker." This is where hypnotherapy excels.
              </p>
              <p>
                My approach uses solution-focused hypnotherapy to rewire the subconscious habits that keep you smoking. We work on changing your mindset from "giving up" something you enjoy to freeing yourself from something that controls you. By addressing the basal ganglia autopilot patterns, the cue-routine-reward loops, and the emotional associations with smoking, hypnotherapy helps you become a non-smoker naturally - without cravings, without irritability, without feeling deprived.
              </p>
              <p>
                I'm honest about results: research shows that at 12 months, approximately 20-30% of motivated clients remain smoke-free with hypnotherapy support. While some studies report higher short-term rates, long-term success is more modest and depends heavily on your genuine motivation to quit. Hypnotherapy isn't magic. It works best when you're truly ready to become a non-smoker. But if you're committed, it's one of the most effective approaches available. The intensive 2-hour session includes lifetime support and a money-back guarantee. To put that in perspective: a 20-a-day smoker spends thousands of pounds a year on cigarettes. You'll recoup the cost of the session in just 3 weeks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              How Quit Smoking Hypnotherapy Works
            </h2>
            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Brain className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Understanding the Habit Neuroscience
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Smoking is controlled by your basal ganglia - the autopilot part of your brain that runs habits without conscious thought. This is why you can light a cigarette without even remembering deciding to do it. Every habit follows a cue-routine-reward loop: stress (cue) → cigarette (routine) → temporary relief (reward). Your brain has learned this pattern so deeply it happens automatically.
                    </p>
                    <p className="text-gray-700">
                      Trying to quit with willpower means fighting your basal ganglia with your prefrontal cortex (conscious control). This works briefly, but willpower depletes throughout the day. By evening, when you're tired and stressed, the autopilot wins and you smoke. Hypnotherapy works differently: it rewires the subconscious habit patterns in your basal ganglia, so you don't need to rely on willpower at all. You simply become a non-smoker naturally.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      The Intensive 2-Hour Session
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Most hypnotherapy for smoking cessation is delivered in a single intensive 2-hour session. This isn't a magic wand that removes all cravings instantly, but it's remarkably effective for changing your mindset and breaking the psychological addiction. The session includes:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Understanding your smoking triggers:</strong> We identify your specific cue-routine-reward patterns and emotional associations with smoking</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Reframing your relationship with smoking:</strong> Shifting from "giving up" to "gaining freedom" - becoming a non-smoker rather than a smoker trying not to smoke</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Hypnotherapy session:</strong> Deep relaxation while we work with your subconscious to rewire smoking habits and strengthen your identity as a non-smoker</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Practical strategies:</strong> Tools for handling stress, social situations, and any remaining triggers without smoking</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Lifetime support:</strong> If you ever feel tempted to smoke again, you can return for a free top-up session</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Heart className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Money-Back Guarantee Removes the Risk
                    </h3>
                    <p className="text-gray-700 mb-4">
                      I offer a straightforward money-back guarantee because I'm confident in the approach. If you attend your intensive session, follow the program recommendations, and return within 3 months still smoking, you'll receive a full refund. No questions asked, no hidden conditions.
                    </p>
                    <p className="text-gray-700">
                      This guarantee is possible because the majority of genuinely motivated clients succeed. It removes the financial risk for you. The only thing you're risking is staying trapped in smoking for the rest of your life, spending thousands of pounds per year on a habit that's slowly killing you. When you look at it that way, the real risk is in not trying.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section - Craigavon Economic Context */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              The Financial Freedom of Quitting Smoking
            </h2>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 mb-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                ROI That Makes Sense for Craigavon Families
              </h3>
              <p className="text-gray-700 mb-6">
                Economic pressures on working-class families in Craigavon make the financial impact of smoking particularly significant. Let's be honest about the money:
              </p>
              <div className="bg-white rounded-xl p-6 mb-6">
                <p className="text-sm text-gray-600 mb-2">Annual Smoking Cost (20/day)</p>
                <p className="text-2xl font-bold text-gray-900 mb-1">Thousands of pounds a year</p>
                <p className="text-sm text-gray-600">Hundreds of pounds every month going up in smoke</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <p className="text-lg font-semibold text-gray-900 mb-3">Your Investment Pays Back In:</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>3 weeks</strong> - The cost of the session is recouped</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>2 months</strong> - You've saved enough to matter for family expenses</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>1 year</strong> - A year of cigarette money could pay for a family holiday, car repairs, debt repayment, emergency fund</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>10 years</strong> - A decade of savings could be a house deposit, children's education, secure retirement</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                For families facing economic challenges in Craigavon, that money each month could transform your financial situation. It's not just about health - though that matters enormously. It's about financial freedom. It's about setting a better example for your children. It's about not smelling of smoke when you walk into a job interview. It's about taking back control of your life and your money.
              </p>
              <p>
                When you frame it this way, the investment in hypnotherapy isn't a cost - it's one of the best financial decisions you can make. And with the money-back guarantee, there's literally no financial risk. The only risk is staying trapped in smoking and watching thousands of pounds burn away every year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Commuter Smoking Patterns */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Breaking Commuter Smoking Patterns
            </h2>
            <div className="bg-white rounded-xl p-8">
              <div className="flex items-start space-x-4">
                <Car className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Craigavon's M1 Commuter Smoking Challenge
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Many Craigavon residents who commute to Belfast for work develop powerful car smoking habits. The M1 journey becomes a trigger zone: cigarettes during the drive, smoking at red lights, stress-triggered smoking in traffic jams. The car becomes your smoking space, and the commute feels impossible without cigarettes.
                  </p>
                  <p className="text-gray-700 mb-4">
                    Hypnotherapy is particularly effective for breaking these commuter smoking patterns. We work on rewiring the subconscious association between driving and smoking, changing how your brain responds to traffic stress without reaching for cigarettes, developing alternative behaviors for the commute (audiobooks, podcasts, breathing exercises), and breaking the automatic hand-to-mouth routine that happens while driving.
                  </p>
                  <p className="text-gray-700">
                    For Craigavon commuters, quitting smoking means reclaiming your car as a smoke-free space, arriving at work feeling fresh rather than smelling of cigarettes, saving money on both cigarettes and car cleaning, and proving to yourself that you can handle commuter stress without nicotine. The M1 journey becomes easier, not harder, when you're not constantly thinking about your next cigarette.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Craigavon Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I'd smoked for 20 years, tried everything to quit. After Sharon's intensive session, I haven't touched a cigarette in 6 months. The biggest change was my M1 commute - I used to smoke the entire way to Belfast. Now I listen to podcasts and actually enjoy the drive. The money I'm saving is incredible."
                </p>
                <p className="font-semibold text-gray-900">J.M., Craigavon - 20-year smoking habit, commuter</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I'd switched from smoking to vaping, thinking it was healthier, but I was just as addicted. Sharon explained how the psychological habit was still there. One session and I'm done with nicotine completely. The cost seemed expensive until I worked out how much I was spending every month on vape liquid. Best investment I ever made."
                </p>
                <p className="font-semibold text-gray-900">P.K., Lurgan - Vaping cessation</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Economic stress in Craigavon is real, and smoking was eating money I couldn't afford. Sharon's ROI explanation made me realize I was spending the equivalent of a weekly food shop on cigarettes. Four months smoke-free and I've saved a serious amount. That money went to paying down debt and getting my kids proper school uniforms. Quitting changed our family finances."
                </p>
                <p className="font-semibold text-gray-900">A.C., Portadown - Financial motivation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Craigavon Quit Smoking
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How much does quit smoking hypnotherapy cost in Craigavon?
                </h3>
                <p className="text-gray-700">
                  The intensive 2-hour quit smoking session includes lifetime support and a money-back guarantee. For clients who prefer a gradual approach, a 3-session package is also available. To put this in perspective: a 20-a-day smoker spends thousands of pounds a year on cigarettes. You recoup the cost of the session in just 3 weeks of not smoking. The financial return alone makes this one of the best investments you can make for Craigavon families facing economic pressures.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What are the real success rates for quit smoking hypnotherapy?
                </h3>
                <p className="text-gray-700">
                  I'm honest about success rates because trust matters. Research shows that at 12 months, approximately 20-30% of motivated clients remain smoke-free with hypnotherapy support. While some studies report higher short-term rates, long-term success is more modest and depends heavily on your genuine motivation to quit. Hypnotherapy isn't magic - it works best when you're truly ready to become a non-smoker. The clients who succeed are those who recognize they're ready for change and use hypnotherapy as the tool to make it happen.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does the money-back guarantee really work?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. If you attend your intensive session, follow the program recommendations, and return within 3 months still smoking, you'll receive a full refund. This guarantee is possible because the majority of genuinely motivated clients succeed. It removes the financial risk and demonstrates my confidence in the approach. The guarantee terms are clear and straightforward - no hidden conditions.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for commuter smoking patterns?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Many Craigavon residents who commute to Belfast via M1 develop strong car smoking habits - cigarettes during the drive, smoking at red lights, stress-triggered smoking in traffic. Hypnotherapy addresses these specific triggers by rewiring the subconscious patterns that link driving stress with cigarettes. We work on changing your association between car commutes and smoking, developing alternative stress management for traffic, and breaking the automatic reach for cigarettes during your drive. The approach is tailored to your specific smoking triggers.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help me quit vaping as well as smoking?
                </h3>
                <p className="text-gray-700">
                  Absolutely. The approach works equally well for vaping cessation as it does for cigarettes. While vaping is marketed as 'healthier' than smoking, it still maintains the nicotine addiction and the psychological habit. Many people who switched from smoking to vaping find themselves just as dependent. The hypnotherapy program addresses both the physical nicotine addiction and the psychological patterns that keep you reaching for your vape throughout the day.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Belfast from Craigavon for quit smoking sessions?
                </h3>
                <p className="text-gray-700">
                  Belfast is approximately 25 miles from Craigavon via the M1 motorway, taking 30-35 minutes by car from Junction 10. The journey is straightforward, and many Craigavon clients find it therapeutic - time to mentally prepare for becoming a non-smoker. The manageable distance makes professional smoking cessation support accessible for residents of Northern Ireland's largest town. Online sessions are also available if you prefer the convenience of attending from home.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Why is the ROI emphasis important for Craigavon families?
                </h3>
                <p className="text-gray-700">
                  Economic pressures on working-class families in Craigavon make the financial impact of smoking particularly significant. A 20-a-day habit costs thousands of pounds a year - that's money that could go toward family expenses, savings, or quality of life improvements. Quitting smoking pays for itself in less than a month. For families facing economic challenges, quitting isn't just about health - it's about financial freedom and setting a better example for children.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default CraigavonQuitSmoking;
